import { useMutation, useQuery } from "@tanstack/react-query";
import { QueryKeyTypes } from "../../../assets/types";
import { LocalStorageHelper } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { BannerDto, SearchBannerDto } from "../../../services/dto";
import { IActionResponse, IActionResult } from "../../../services/proxies";
import { ViewProps } from "./props";
import { BannerClient } from "../../../services/proxies/popHub/bannerClient";
import { resolveBannerCommunityId } from "./bannerCommunity";

export namespace BannerService {
    const bannerClient = new BannerClient();

    export const saveBanner = () =>
        useMutation<IActionResult<BannerDto>, Error, BannerDto>({
            mutationFn: async (payload: BannerDto): Promise<IActionResult<BannerDto>> => {
                return await bannerClient.saveBannerAsync(payload);
            },
        });

    const resolveCommunityId = (communityId?: string | null): string | undefined =>
        resolveBannerCommunityId(communityId, LocalStorageHelper.getCommunityId());

    const isValidCommunityId = (communityId: string | undefined): boolean =>
        Boolean(communityId) && communityId !== Constants.emptyGuid;

    export const getBannerQuery = (bannerId: string | undefined) =>
        useQuery({
            queryKey: [ViewProps.UserQueryKeys[QueryKeyTypes.Get], bannerId],
            queryFn: async () => await bannerClient.getBannerAsync(bannerId),
            enabled: Boolean(bannerId) && bannerId !== Constants.emptyGuid,
        });

    export const gridQuery = ( communityId: string | null | undefined, searchQuery: SearchBannerDto ) => {
        const resolvedCommunityId = resolveCommunityId(communityId);
        const payload: SearchBannerDto = { ...searchQuery };

        return useQuery({
            queryKey: [ViewProps.UserQueryKeys[QueryKeyTypes.Grid], resolvedCommunityId, searchQuery],
            enabled: isValidCommunityId(resolvedCommunityId),
            queryFn: async () =>
                await bannerClient.searchBannerAsync(resolvedCommunityId, payload),
            placeholderData: (previousData) => previousData,
        });
    };

    export const getLookups = (communityId?: string | null) => {
        const resolvedCommunityId = resolveCommunityId(communityId);

        return useQuery({
            queryKey: [ViewProps.UserQueryKeys[QueryKeyTypes.Lookup], resolvedCommunityId],
            enabled: isValidCommunityId(resolvedCommunityId),
            queryFn: async (): Promise<ViewProps.BannerTypeLookupProps> =>
                await bannerClient.getBannerLookupsAsync(resolvedCommunityId),
        });
    };

    export const getBannerLocationLookups = (communityId?: string | null) => {
        const resolvedCommunityId = resolveCommunityId(communityId);

        return useQuery({
            queryKey: [
                ViewProps.UserQueryKeys[QueryKeyTypes.Lookup],
                "bannerLocation",
                resolvedCommunityId,
            ],
            enabled: isValidCommunityId(resolvedCommunityId),
            queryFn: async () =>
                await bannerClient.getBannerLocationLookupsAsync(resolvedCommunityId),
            staleTime: 10 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
            placeholderData: (previousData) => previousData,
        });
    };

    export const deleteBanner = (id?: string | null) =>
        useMutation<IActionResponse, Error, string>({
            mutationFn: async (id: string | undefined) => {
                return await bannerClient.deleteBannerAsync(id);
            },
        });
}